import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { ApiError } from "@/api/api-axios";

interface ZaloLoginButtonProps {
  rememberMe: boolean;
  isZaloLoading: boolean;
  setIsZaloLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  disabled?: boolean;
  callbackUrl?: string;
}

export default function ZaloLoginButton({
  rememberMe,
  isZaloLoading,
  setIsZaloLoading,
  setError,
  disabled = false,
  callbackUrl,
}: ZaloLoginButtonProps) {
  const { loginWithZalo } = useAuth();
  const location = useLocation();
  const popupRef = useRef<Window | null>(null);
  const checkPopupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleZaloLogin = useCallback(() => {
    setIsZaloLoading(true);
    setError("");

    const ZALO_APP_ID = import.meta.env.VITE_ZALO_APP_ID || "";
    if (!ZALO_APP_ID) {
      setError("Zalo App ID chưa được cấu hình");
      setIsZaloLoading(false);
      return;
    }

    // Tạo state parameter để bảo mật (CSRF protection)
    const state =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("zalo_oauth_state", state);

    // Lấy redirect URI từ window.location.origin để hỗ trợ cả dev và production
    const redirectUri = `${window.location.origin}/zalocallback`;
    const encodedRedirectUri = encodeURIComponent(redirectUri);

    const zaloAuthUrl =
      `https://oauth.zaloapp.com/v4/permission?app_id=${ZALO_APP_ID}` +
      `&redirect_uri=${encodedRedirectUri}` +
      `&response_type=code` +
      `&scope=basic` +
      `&state=${state}`;

    // Mở popup window thay vì redirect trực tiếp
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      zaloAuthUrl,
      "Zalo Login",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );

    if (!popup) {
      setError("Popup bị chặn. Vui lòng cho phép popup và thử lại.");
      setIsZaloLoading(false);
      return;
    }

    popupRef.current = popup;

    // Lắng nghe message từ popup (callback page sẽ gửi message)
    const handleMessage = async (event: MessageEvent) => {
      // Kiểm tra origin để bảo mật
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "ZALO_AUTH_SUCCESS") {
        if (popupRef.current) {
          popupRef.current.close();
        }
        window.removeEventListener("message", handleMessage);

        // Xóa interval kiểm tra popup
        if (checkPopupIntervalRef.current) {
          clearInterval(checkPopupIntervalRef.current);
          checkPopupIntervalRef.current = null;
        }

        // Xác thực state parameter
        const storedState = sessionStorage.getItem("zalo_oauth_state");
        const receivedState = event.data.state;

        if (storedState && receivedState && storedState !== receivedState) {
          sessionStorage.removeItem("zalo_oauth_state");
          setError("Xác thực không hợp lệ. Vui lòng thử lại.");
          setIsZaloLoading(false);
          return;
        }

        // Xóa state sau khi xác thực thành công
        sessionStorage.removeItem("zalo_oauth_state");

        // Xử lý code nhận được từ Zalo
        const code = event.data.code;
        console.log("Zalo auth code:", code);

        try {
          // Gọi API để đổi code lấy token và đăng nhập
          const result = await loginWithZalo(code, rememberMe);

          if (result.success) {
            // Ưu tiên callbackUrl từ props, sau đó location.state, cuối cùng là mặc định
            const redirectUrl = callbackUrl || location.state?.from?.pathname || "/dashboard";
            // Sử dụng setTimeout để đợi state được cập nhật trước khi navigate
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 100);
          } else {
            setError(result.message);
          }
        } catch (error: unknown) {
          const message = (error as ApiError).data?.message as string || "Đăng nhập Zalo thất bại";
          setError(message);
        } finally {
          setIsZaloLoading(false);
        }
      } else if (event.data.type === "ZALO_AUTH_ERROR") {
        if (popupRef.current) {
          popupRef.current.close();
        }
        window.removeEventListener("message", handleMessage);
        sessionStorage.removeItem("zalo_oauth_state");
        setError(event.data.error || "Đăng nhập Zalo thất bại");
        setIsZaloLoading(false);

        // Xóa interval kiểm tra popup
        if (checkPopupIntervalRef.current) {
          clearInterval(checkPopupIntervalRef.current);
          checkPopupIntervalRef.current = null;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Kiểm tra nếu popup bị đóng mà chưa có kết quả
    checkPopupIntervalRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        if (checkPopupIntervalRef.current) {
          clearInterval(checkPopupIntervalRef.current);
          checkPopupIntervalRef.current = null;
        }
        window.removeEventListener("message", handleMessage);
        sessionStorage.removeItem("zalo_oauth_state");
        setIsZaloLoading(false);
      }
    }, 500);
  }, [
    rememberMe,
    location,
    callbackUrl,
    loginWithZalo,
    setIsZaloLoading,
    setError,
  ]);

  // Cleanup khi component unmount
  useEffect(() => {
    return () => {
      if (checkPopupIntervalRef.current) {
        clearInterval(checkPopupIntervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  return (
    <Button
      type="button"
      disabled={disabled}
      variant="outline"
      onClick={handleZaloLogin}
      className="w-full h-10 text-gray-600 hover:bg-blue-50 hover:border-blue-200"
    >
      <img src="/assets/zalo.png" alt="Zalo" className="w-4 h-4" />
      {isZaloLoading ? (
        <>
          <Loader2 className="ml-2 h-4 w-4 animate-spin" />
          <span className="ml-1">Đang xử lý</span>
        </>
      ) : (
        "Zalo"
      )}
    </Button>
  );
}

